/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Formik, Field as FormikField, FormikProps, FormikErrors } from 'formik'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Collapse } from 'react-bootstrap'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { Select, OptionType } from '../../components/Select'
import { FsiLifecycleState } from '../../constants/fsi'
import { File as FileConstants } from '../../constants/file'
import { USER_ROLE } from '../../constants/user'
import { PageConfiguration, FolderDetails } from './types'
import useDocumentsListService from '../../hooks/useDocumentsListService'

interface DocumentsTableFilterProps {
  fsi: FolderDetails
  isFilterCollapsed: boolean
  configuration: PageConfiguration
}

interface FilterValues {
  name: string
  lastUpdated: string
  state: string
  blindingStatus: string
  artifacts: string[]
  lastUpdatedFrom: Date | null
  lastUpdatedTo: Date | null
  artifactId?: string | number
}

interface LinkedEntities {
  entityLinkMap: {
    [key: string]: string | number
  }
}

interface ArtifactDefinition {
  definition?: {
    options?: string[]
  }
  plain?: () => ArtifactDefinition
}

const BlindingStatus = {
  ALL: '',
  UNBLINDED: 'UNBLINDED',
  BLINDED: 'BLINDED'
} as const

export const DocumentsTableFilter: React.FC<DocumentsTableFilterProps> = ({
  fsi,
  isFilterCollapsed,
  configuration
}) => {
  const context = useServiceContext()
  const {
    service: {
      filter: { translate },
      auth,
      access,
      persist,
      init
    },
    api: { etmf, metadata },
    constant
  } = context

  const tableData = useRcmTableData()
  const { assembleRegularFolderFiltersFromFormValues } = useDocumentsListService()
  const persistenceKey = tableData.persistenceKey + '_filter'

  // Get feature flags and user role
  const featuresInfo = init?.getFeaturesInfo?.() || {}
  const isEnableDocDetailsExpirationDate = featuresInfo?.isEnableDocDetailsExpirationDate ?? false
  const USER_ROLE_VALUE = auth.getRole()

  // Memoize defaultFilter to prevent recreation on every render
  const defaultFilter: FilterValues = useMemo(
    () => ({
      name: '',
      lastUpdated: '', // '' means 'Anytime'
      state: FsiLifecycleState.COMPLETED, // Default to 'Completed' state
      blindingStatus: '',
      artifacts: [],
      lastUpdatedFrom: null,
      lastUpdatedTo: null
    }),
    []
  )

  const [filter, setFilter] = useState<FilterValues | null>(null)
  const [isBlindingFilterVisible, setIsBlindingFilterVisible] = useState(false)
  const [isSectionFilterVisible, setIsSectionFilterVisible] = useState(false)
  const [artifactOptions, setArtifactOptions] = useState<OptionType[]>([])
  const [linkedEntitiesData, setLinkedEntitiesData] = useState<LinkedEntities | null>(null)
  const formikRef = React.useRef<FormikProps<FilterValues> | null>(null)
  const hasInitializedRef = React.useRef(false) // Track if initialization has happened

  // Last Updated options (static)
  const lastUpdatedOptions = useMemo(
    () => [
      { value: '', label: translate('ANYTIME') },
      { value: 'p1h', label: translate('PAST_HOUR') },
      { value: 'p24h', label: translate('PAST_24_HOURS') },
      { value: 'p1w', label: translate('PAST_WEEK') },
      { value: 'p1m', label: translate('PAST_MONTH') },
      { value: 'p1y', label: translate('PAST_YEAR') },
      { value: 'range', label: translate('CUSTOM_RANGE') }
    ],
    [translate]
  )

  // State options - dynamic based on user role, feature flags, and permissions
  const stateOptions = useMemo(() => {
    const folderFeatureType = fsi?.folderFeatureType
    const folderName = fsi?.name

    // Special handling for ETMF_SPONSOR role with ETMF folder feature type
    if (USER_ROLE_VALUE === USER_ROLE.ETMF_SPONSOR && folderFeatureType === FileConstants.folderFeatureType.ETMF) {
      // For "Uploaded Documents" or "Documents for Site" folders
      if (folderName === 'Uploaded Documents' || folderName === 'Documents for Site') {
        return [
          { value: '', label: translate('OPTION_ALL_STATES') },
          { value: FsiLifecycleState.COMPLETED, label: translate('STATE_' + FsiLifecycleState.COMPLETED) },
          { value: FsiLifecycleState.REJECTED, label: translate('STATE_' + FsiLifecycleState.REJECTED) }
        ]
      }
      // For other folders, only show COMPLETED
      return [{ value: FsiLifecycleState.COMPLETED, label: translate('STATE_' + FsiLifecycleState.COMPLETED) }]
    }

    // Standard state options for other roles
    const baseStates: OptionType[] = [
      { value: '', label: translate('OPTION_ALL_STATES') },
      { value: FsiLifecycleState.COMPLETED, label: translate('STATE_' + FsiLifecycleState.COMPLETED) }
    ]

    // Add EXPIRED state if feature flag is enabled
    if (isEnableDocDetailsExpirationDate) {
      baseStates.push({ value: FsiLifecycleState.EXPIRED, label: translate('STATE_' + FsiLifecycleState.EXPIRED) })
    }

    // Add REJECTED and OBSOLETE if user has permission
    if (access.isAllowed('documentsFilterObsolete') || access.isAllowed('documentsFilterRejected')) {
      baseStates.push(
        { value: FsiLifecycleState.REJECTED, label: translate('STATE_' + FsiLifecycleState.REJECTED) },
        { value: FsiLifecycleState.OBSOLETE, label: translate('STATE_' + FsiLifecycleState.OBSOLETE) }
      )
    }

    return baseStates
  }, [USER_ROLE_VALUE, fsi?.folderFeatureType, fsi?.name, isEnableDocDetailsExpirationDate, access, translate])

  // Helper function to assemble persisted filter values to form values
  const assembleRegularFolderFiltersToFormValues = useCallback(
    (persistedFilter: any): FilterValues => {
      const formValues: FilterValues = { ...defaultFilter }

      if (persistedFilter.name) {
        formValues.name = persistedFilter.name
      }

      if (persistedFilter.state) {
        formValues.state = persistedFilter.state
      }

      if (persistedFilter.blindingStatus) {
        formValues.blindingStatus = persistedFilter.blindingStatus
      }

      if (persistedFilter.artifacts && Array.isArray(persistedFilter.artifacts)) {
        formValues.artifacts = persistedFilter.artifacts
      }

      if (persistedFilter.lastUpdated) {
        formValues.lastUpdated = persistedFilter.lastUpdated
      }

      if (persistedFilter.lastUpdatedFrom) {
        formValues.lastUpdatedFrom = new Date(persistedFilter.lastUpdatedFrom)
      }

      if (persistedFilter.lastUpdatedTo) {
        formValues.lastUpdatedTo = new Date(persistedFilter.lastUpdatedTo)
      }

      return formValues
    },
    [defaultFilter]
  )

  // Initialize filter from persistence on mount - only runs ONCE
  useEffect(() => {
    // Skip if already initialized to prevent re-running on dependency changes
    if (hasInitializedRef.current) {
      return
    }
    hasInitializedRef.current = true

    const persistedFilter = persist.getGlobal<FilterValues>(persistenceKey)
    const newFilter = persistedFilter ? assembleRegularFolderFiltersToFormValues(persistedFilter) : defaultFilter

    setFilter(newFilter)
    // Apply initial filter to table
    tableData.applyFilter(newFilter)
  }, [persist, persistenceKey, assembleRegularFolderFiltersToFormValues, defaultFilter, tableData])

  // Handle fsi changes - fetch linked entities and determine filter visibility
  useEffect(() => {
    if (!fsi?.id) {
      return
    }

    const removeArtifactsFilter = () => {
      setArtifactOptions([])
    }

    // Fetch linked entities to determine section folder type and artifact options
    etmf
      .getLinkedEntities()
      .then((linkedEntities: LinkedEntities) => {
        setLinkedEntitiesData(linkedEntities)

        const sectionFolderTypeId = linkedEntities.entityLinkMap?.SECTION_FOLDER_TYPE

        // Determine if blinding filter should be visible (for RCR or ETMF folders)
        const shouldShowBlindingFilter =
          fsi?.folderFeatureType === FileConstants.folderFeatureType.RCR ||
          fsi?.folderFeatureType === FileConstants.folderFeatureType.ETMF

        // Determine if section filter should be visible
        const shouldShowSectionFilter = fsi?.fsiTypeTemplate && fsi.fsiTypeTemplate.id === sectionFolderTypeId

        setIsBlindingFilterVisible(shouldShowBlindingFilter)
        setIsSectionFilterVisible(shouldShowSectionFilter)

        // Fetch artifact options if this is a section folder
        if (shouldShowSectionFilter && linkedEntities.entityLinkMap) {
          const artifactId = linkedEntities.entityLinkMap[constant?.etmf?.property?.ARTIFACT || 'ARTIFACT']

          if (artifactId && fsi.id) {
            metadata
              .getArtifactValues(Number(artifactId), fsi.id)
              .then((data: unknown) => {
                const typedData = data as ArtifactDefinition
                const artifactData = typedData?.plain ? typedData.plain() : typedData
                const options = artifactData?.definition?.options || []
                setArtifactOptions(
                  options.map((item: string) => ({
                    value: item,
                    label: item
                  }))
                )
              })
              .catch(() => {
                removeArtifactsFilter()
              })
          } else {
            removeArtifactsFilter()
          }
        } else {
          removeArtifactsFilter()
        }
      })
      .catch(() => {
        setIsBlindingFilterVisible(false)
        setIsSectionFilterVisible(false)
        removeArtifactsFilter()
      })
  }, [fsi?.id, fsi?.folderFeatureType, fsi?.fsiTypeTemplate, etmf, metadata, constant])

  // Apply filter with artifactId from linked entities
  const doFilter = useCallback(
    (values: FilterValues) => {
      const filterValues = { ...values }

      // Add artifactId from linked entities if available
      if (linkedEntitiesData?.entityLinkMap) {
        filterValues.artifactId = linkedEntitiesData.entityLinkMap[constant?.etmf?.property?.ARTIFACT || 'ARTIFACT']
      }

      tableData.applyFilter(filterValues)

      // Persist filter values
      persist.setGlobal(persistenceKey, { ...values })
    },
    [tableData, persist, persistenceKey, linkedEntitiesData, constant]
  )

  // Validate form values
  const validateForm = useCallback(
    (values: FilterValues): FormikErrors<FilterValues> => {
      const errors: FormikErrors<FilterValues> = {}

      // Validate date range when custom range is selected
      if (values.lastUpdated === 'range') {
        if (!values.lastUpdatedFrom) {
          errors.lastUpdatedFrom = translate('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR') || 'Required'
        }
        if (!values.lastUpdatedTo) {
          errors.lastUpdatedTo = translate('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR') || 'Required'
        }
      }

      return errors
    },
    [translate]
  )

  // Handle form reset
  const handleReset = useCallback(() => {
    setFilter(defaultFilter)
    doFilter(defaultFilter)
    // Reset Formik form to default values
    if (formikRef.current) {
      formikRef.current.resetForm({ values: defaultFilter })
    }
  }, [defaultFilter, doFilter])

  // Check if current filter values differ from default
  const isFilterModified = useCallback(
    (values: FilterValues) => {
      return (
        values.name !== defaultFilter.name ||
        values.lastUpdated !== defaultFilter.lastUpdated ||
        values.state !== defaultFilter.state ||
        values.blindingStatus !== defaultFilter.blindingStatus ||
        values.artifacts.length !== defaultFilter.artifacts.length ||
        values.lastUpdatedFrom !== defaultFilter.lastUpdatedFrom ||
        values.lastUpdatedTo !== defaultFilter.lastUpdatedTo
      )
    },
    [defaultFilter]
  )

  // Determine collapse state based on configuration (matching legacy behavior)
  const shouldCollapse =
    configuration.title !== 'BROWSER_DOCUMENTS_TITLE' ? tableData.isFilterCollapsed : isFilterCollapsed

  return (
    <Collapse in={!shouldCollapse}>
      <div>
        <div className="filter-wrapper">
          {filter && (
            <Formik
              innerRef={formikRef}
              initialValues={{
                ...filter
              }}
              validate={validateForm}
              enableReinitialize={true}
              onReset={() => {
                setFilter(defaultFilter)
              }}
              onSubmit={(values) => {
                // Only call doFilter - do NOT call setFilter(values) here
                // Formik manages form state internally. Calling setFilter would trigger
                // a re-render with new initialValues, causing enableReinitialize to reset the form.
                doFilter(values)
              }}
            >
              {({ values, handleSubmit, handleReset: formikHandleReset, setFieldValue, errors, touched }) => {
                return (
                  <>
                    <div className="row">
                      <div className="col-xs-4">
                        <div className="form-group">
                          <label className="filter-label">{translate('LAST_UPDATED')}</label>
                          <Select
                            name="lastUpdated"
                            value={values.lastUpdated}
                            options={lastUpdatedOptions}
                            isClearable={false}
                            onChange={(option: any) => {
                              if (option.value !== 'range') {
                                setFieldValue('lastUpdatedTo', null)
                                setFieldValue('lastUpdatedFrom', null)
                              }
                              setFieldValue('lastUpdated', option ? option.value : null)
                            }}
                            searchable
                          />
                        </div>

                        {values.lastUpdated === 'range' && (
                          <div className="custom-range-filters" style={{ paddingLeft: '26px', marginTop: '-4px' }}>
                            <div className="form-group">
                              <label className="filter-label">{translate('SEARCH_FROM')}</label>
                              <DatePicker
                                id="last-updated-from"
                                name="lastUpdatedFrom"
                                selected={values.lastUpdatedFrom}
                                onChange={(date) => setFieldValue('lastUpdatedFrom', date)}
                                maxDate={values.lastUpdatedTo || new Date()}
                                todayButton="Today"
                                dateFormat="MMM dd, yyyy"
                                showYearDropdown
                                className="form-control field-control w100p"
                              />
                              {errors.lastUpdatedFrom && touched.lastUpdatedFrom && (
                                <div className="text-danger error-message">{errors.lastUpdatedFrom}</div>
                              )}
                            </div>
                            <div className="form-group">
                              <label className="filter-label">{translate('SEARCH_TO')}</label>
                              <DatePicker
                                id="last-updated-to"
                                name="lastUpdatedTo"
                                selected={values.lastUpdatedTo}
                                onChange={(date) => setFieldValue('lastUpdatedTo', date)}
                                minDate={values.lastUpdatedFrom}
                                maxDate={new Date()}
                                todayButton="Today"
                                dateFormat="MMM dd, yyyy"
                                showYearDropdown
                                className="form-control field-control w100p"
                              />
                              {errors.lastUpdatedTo && touched.lastUpdatedTo && (
                                <div className="text-danger error-message">{errors.lastUpdatedTo}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="col-xs-4">
                        <div className="form-group">
                          <label className="filter-label">{translate('BROWSER_FILTER_FOLDERDOC_NAME')}</label>
                          <FormikField type="text" name="name" className="form-control field-control" autoFocus />
                        </div>
                      </div>
                      <div className="col-xs-4">
                        <div className="form-group">
                          <label className="filter-label">{translate('STATE')}</label>
                          <Select
                            name="state"
                            value={values.state}
                            options={stateOptions}
                            isClearable={false}
                            onChange={(option: any) => {
                              setFieldValue('state', option ? option.value : null)
                            }}
                            searchable
                          />
                        </div>
                      </div>
                    </div>

                    {/* Blinding Status and Artifacts Filter Row */}
                    {(isBlindingFilterVisible || isSectionFilterVisible) && (
                      <div className="row">
                        {/* Blinding Status Filter */}
                        {isBlindingFilterVisible && (
                          <div className="col-xs-4">
                            <div className="form-group">
                              <label className="filter-label">{translate('BLINDING_STATUS_LABEL')}</label>
                              {Object.keys(BlindingStatus).map((blindingStatusKey) => {
                                const blindingStatusValue =
                                  BlindingStatus[blindingStatusKey as keyof typeof BlindingStatus]
                                return (
                                  <div key={blindingStatusKey} className="radio classic mb10 w100p">
                                    <label className="p-0">
                                      <input
                                        type="radio"
                                        name="blindingStatus"
                                        value={blindingStatusValue}
                                        checked={values.blindingStatus === blindingStatusValue}
                                        onChange={(event) => {
                                          setFieldValue('blindingStatus', event.target.value)
                                        }}
                                      />
                                      <div className="px-3">
                                        {translate(
                                          blindingStatusKey === 'ALL' ? 'ALL' : 'BLINDING_STATUS_' + blindingStatusKey
                                        )}
                                      </div>
                                    </label>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}

                        {/* Artifacts (Section) Filter */}
                        {isSectionFilterVisible && (
                          <div className="col-xs-4">
                            <div className="form-group">
                              <label className="filter-label">{translate('TABLE_TH_ARTIFACT_NO')}</label>
                              <Select
                                placeholder={translate('ITEM(S)...')}
                                name="artifacts"
                                value={values.artifacts}
                                options={artifactOptions}
                                onChange={(option: any) => {
                                  const selectedValues = option
                                    ? Array.isArray(option)
                                      ? option.map((opt: OptionType) => opt.value)
                                      : [option.value]
                                    : []
                                  setFieldValue('artifacts', selectedValues)
                                }}
                                multi={true}
                                searchable
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <button
                        type="button"
                        data-action="filter"
                        disabled={tableData.isLoading}
                        className="btn btn-primary"
                        onClick={() => handleSubmit()}
                      >
                        {translate('BTN_FILTER')}
                      </button>
                      <button
                        type="button"
                        data-action="reset"
                        className="btn btn-chita btn-link"
                        disabled={!isFilterModified(values)}
                        onClick={() => {
                          handleReset()
                        }}
                      >
                        {translate('BTN_CLEAR_FILTER')}
                      </button>
                    </div>
                  </>
                )
              }}
            </Formik>
          )}
        </div>
      </div>
    </Collapse>
  )
}
